import { Fragment, type ReactNode } from 'react';

/* ---------------------------------------------------------------------------
   Markdown "קל" לתשובות העוזר: פסקאות, שורות, רשימות (- / * / 1.), **מודגש**
   ו-`קוד`. נבנה מאלמנטים של React — אף פעם לא innerHTML — כך שטקסט מהמודל
   (או ממה שהמשתמש הדביק) לא יכול להזריק HTML.
   --------------------------------------------------------------------------- */

function inline(text: string, keyPrefix: string): ReactNode[] {
  const out: ReactNode[] = [];
  const re = /(\*\*[^*]+\*\*|`[^`]+`)/g;
  let last = 0;
  let i = 0;
  for (const m of text.matchAll(re)) {
    if (m.index > last) out.push(text.slice(last, m.index));
    const token = m[0];
    out.push(
      token.startsWith('**') ? (
        <strong key={`${keyPrefix}-${i++}`} className="font-semibold">
          {token.slice(2, -2)}
        </strong>
      ) : (
        <code key={`${keyPrefix}-${i++}`} className="rounded bg-surface-sunken px-1 text-[0.9em]">
          {token.slice(1, -1)}
        </code>
      ),
    );
    last = m.index + token.length;
  }
  if (last < text.length) out.push(text.slice(last));
  return out;
}

export function MarkdownLite({ text }: { text: string }) {
  const blocks = text.trim().split(/\n{2,}/);
  return (
    <div className="space-y-2">
      {blocks.map((block, b) => {
        const lines = block.split('\n');
        const bullet = lines.every((l) => /^\s*([-*•]|\d+[.)])\s+/.test(l));
        if (bullet) {
          const ordered = /^\s*\d/.test(lines[0] ?? '');
          const items = lines.map((l, i) => (
            <li key={i}>{inline(l.replace(/^\s*([-*•]|\d+[.)])\s+/, ''), `${b}-${i}`)}</li>
          ));
          return ordered ? (
            <ol key={b} className="list-decimal space-y-0.5 ps-5">
              {items}
            </ol>
          ) : (
            <ul key={b} className="list-disc space-y-0.5 ps-5">
              {items}
            </ul>
          );
        }
        return (
          <p key={b} className="whitespace-pre-wrap">
            {lines.map((l, i) => (
              <Fragment key={i}>
                {i > 0 ? <br /> : null}
                {inline(l, `${b}-${i}`)}
              </Fragment>
            ))}
          </p>
        );
      })}
    </div>
  );
}
